import {
    DTOCardFormField, NestedField,
} from 'app/types/form.types'
import {
    ForwarderCategory,
} from 'app/enums/label.enums'
import {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'
import {
    TypeIcon,
} from 'app/shared-components/NotificationIcon/notificationIcon.types'

import {
    FreightForwarder,
    ImportInstructions,
} from '../FreightForwarders.types'

type FreightForwarderFull = FreightForwarder & ImportInstructions

const getFreightForwarderDetailsFields = (
    {
        getAllowedValues, createLocationLink, disabled,
    }: {
        getAllowedValues: (enumType: any) => Record<string, string>,
        createLocationLink: string,
        disabled: boolean,
    },
): DTOCardFormField<keyof FreightForwarder> => {
    return {
        id: 'freightForwarderDetails',
        title: 'Freight Forwarder Details',
        className: 'freightForwarderDetails',
        'data-testid': 'card-freightForwarderDetails',
        fields: [
            {
                id: 'freightForwarderDetails-column1',
                fields: [
                    {
                        name: 'freightForwarderName',
                        labelKey: 'FreightForwarders.name',
                        componentName: 'InputSingleline',
                        required: true,
                    },
                    {
                        noLabel: true,
                        componentName: 'InputRule',
                        rule: 'Name + City + Country Code',
                        example: 'Example: Kuehne & Nagel Mechelen (BE)',
                    },
                    {
                        name: 'company',
                        labelKey: 'FreightForwarders.company',
                        componentName: 'CompanyObjectSelector',
                        required: true,
                    },
                    {
                        name: 'addressOfReference',
                        componentName: 'LocationObjectSelector',
                        required: true,
                        labelKey: 'FreightForwarders.addressOfReference',
                    },
                    ...disabled ? [] : [{
                        name: 'id',
                        componentName: 'SkyNetLink',
                        link: createLocationLink,
                        text: 'Location is missing? Create',
                        type: TypeLink.EXTERNAL,
                        size: SizeLink.S,
                        className: 'createLocationLink',
                    }] as NestedField<keyof FreightForwarder>[],
                    {
                        name: 'contacts',
                        labelKey: 'FreightForwarders.contacts',
                        componentName: 'MultipleContactObjectSelector',
                        required: true,
                    },
                ],
            },
            {
                id: 'freightForwarderDetails-column2',
                fields: [
                    {
                        name: 'freightRateServiceProviderCode',
                        labelKey: 'FreightForwarders.freightRateServiceProviderCode',
                        componentName: 'InputSingleline',
                        required: true,
                    },
                    {
                        name: 'forwarderCategory',
                        labelKey: 'FreightForwarders.forwarderCategory',
                        componentName: 'EnumChipSelector',
                        allowedValues: getAllowedValues(ForwarderCategory),
                        required: true,
                    },
                    {
                        name: 'sapBusinessPartnerNumber',
                        componentName: 'InputSingleline',
                        notificationIconProps: {
                            content: 'Please reach out to Finance to receive a value for this field.',
                            type: TypeIcon.INFO,
                            tooltipPlacement: 'right',
                        },
                        required: true,
                        labelKey: 'BillingCompanyView.sapBusinessPartnerNumber',
                    },
                    {
                        name: 'temperatureControlledAwardedPartner',
                        labelKey: 'FreightForwarders.temperatureControlledAwardedPartner',
                        componentName: 'Checkbox',
                    },
                ],
            },
        ],
    }
}

const getImportInstructionsFields = (): DTOCardFormField<keyof ImportInstructions> => {
    return {
        id: 'importInstructions',
        title: 'Import Instructions',
        className: 'importInstructions',
        'data-testid': 'card-importInstructions',
        fields: [
            {
                id: 'importInstructions-column1',
                fields: [
                    {
                        name: 'masterConsignee',
                        labelKey: 'FreightForwarders.importInstructionsMasterConsignee',
                        componentName: 'LongText',
                        required: true,
                    },
                    {
                        name: 'masterAdditionalInfo',
                        labelKey: 'FreightForwarders.importInstructionsMasterAdditionalInfo',
                        componentName: 'LongText',
                    },
                ],
            },
            {
                id: 'importInstructions-column2',
                fields: [
                    {
                        name: 'houseConsignee',
                        labelKey: 'FreightForwarders.importInstructionsHouseConsignee',
                        componentName: 'LongText',
                    },
                    {
                        name: 'houseAdditionalInfo',
                        labelKey: 'FreightForwarders.importInstructionsHouseAdditionalInfo',
                        componentName: 'LongText',
                    },
                ],
            },
        ],
    }
}

const getFreightForwarderCreateFields = (
    {
        getAllowedValues,
        showImportInstructions,
        createLocationLink,
        disabled,
    }: {
        getAllowedValues: (enumType: any) => Record<string, string>,
        showImportInstructions: boolean,
        createLocationLink: string,
        disabled: boolean,
    },
): DTOCardFormField<keyof FreightForwarderFull>[] => {
    if (showImportInstructions) {
        return [
            getFreightForwarderDetailsFields({
                getAllowedValues, createLocationLink, disabled,
            }),
            getImportInstructionsFields(),
        ]
    }
    return [getFreightForwarderDetailsFields({
        getAllowedValues, createLocationLink, disabled,
    })]
}

export default getFreightForwarderCreateFields
